import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from '../../../context/FirebaseContext';
import './AdminLoginPage.css';
import './AdminRegisterPage.css';

const AdminRegisterPage = () => {
    // Basic account info
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [ownerName, setOwnerName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');

    // Store information
    const [storeName, setStoreName] = useState('');
    const [storeDescription, setStoreDescription] = useState('');
    const [storeAddress, setStoreAddress] = useState('');

    // Form state
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [step, setStep] = useState(1); // 1: Account, 2: Store
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [slideDirection, setSlideDirection] = useState(''); // 'left' or 'right'

    const {
        currentUser,
        isAuthenticatedAdmin,
        register
    } = useFirebase();

    const navigate = useNavigate();

    // Redirect if already authenticated
    useEffect(() => {
        const checkAuth = async () => {
            if (currentUser) {
                const isValid = await isAuthenticatedAdmin();
                if (isValid) {
                    navigate('/admin/dashboard', { replace: true });
                }
            }
        };

        checkAuth();
    }, [currentUser, isAuthenticatedAdmin, navigate]);

    const validateStep = (currentStep) => {
        setError('');

        if (currentStep === 1) {
            if (!email.trim() || !password || !confirmPassword || !ownerName.trim()) {
                setError('Please fill in all required fields');
                return false;
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email.trim())) {
                setError('Please enter a valid email address');
                return false;
            }

            if (password.length < 6) {
                setError('Password must be at least 6 characters long');
                return false;
            }

            if (password !== confirmPassword) {
                setError('Passwords do not match');
                return false;
            }
        }

        if (currentStep === 2) {
            if (!storeName.trim()) {
                setError('Store name is required');
                return false;
            }
        }

        return true;
    };

    const handleNext = () => {
        if (validateStep(step)) {
            setSlideDirection('slide-left');
            setTimeout(() => {
                setStep(step + 1);
                setSlideDirection('slide-in-right');
            }, 200);
        }
    };

    const handlePrevious = () => {
        setError('');
        setSlideDirection('slide-right');
        setTimeout(() => {
            setStep(step - 1);
            setSlideDirection('slide-in-left');
        }, 200);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        if (!validateStep(2)) return;

        setLoading(true);

        try {
            const registrationData = {
                email: email.trim(),
                password,
                ownerName: ownerName.trim(),
                phoneNumber: phoneNumber.trim(),
                storeName: storeName.trim(),
                storeDescription: storeDescription.trim(),
                storeAddress: storeAddress.trim()
            };

            const result = await register(registrationData);

            if (result.registrationComplete) {
                // Show success modal instead of alert
                setSuccessMessage(result.message || "Your store has been created successfully!");
                setShowSuccessModal(true);
            } else {
                // Fallback - shouldn't happen with new logic
                navigate('/admin/dashboard', { replace: true });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoToLogin = () => {
        setShowSuccessModal(false);
        navigate('/admin/login', { replace: true });
    };

    return (
        <div className="register-container">
            {/* Decorative background elements */}
            <div className="register-bg-shapes">
                <div className="shape shape-1"></div>
                <div className="shape shape-2"></div>
                <div className="shape shape-3"></div>
            </div>

            <div className="register-card">
                <div className="register-header">
                    <div className="register-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <h1>Create Your Store</h1>
                    <p className="register-subtitle">Set up your online store in just 2 simple steps</p>

                    {/* Enhanced Progress indicator */}
                    <div className="register-progress">
                        <div className="progress-track">
                            <div
                                className="progress-fill"
                                style={{ width: step === 1 ? '50%' : '100%' }}
                            ></div>
                        </div>
                        <div className="progress-steps">
                            <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                                <div className="step-indicator">
                                    {step > 1 ? (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                            <polyline points="20,6 9,17 4,12"></polyline>
                                        </svg>
                                    ) : '1'}
                                </div>
                                <span>Account Details</span>
                            </div>
                            <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
                                <div className="step-indicator">2</div>
                                <span>Store Info</span>
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="register-error">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <span>{error}</span>
                    </div>
                )}

                <form className="register-form" onSubmit={handleSubmit}>
                    {/* Step 1: Account Information */}
                    {step === 1 && (
                        <div className={`form-step-container ${slideDirection}`}>
                            <div className="form-step-header">
                                <div className="step-icon account-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="12" cy="7" r="4"></circle>
                                    </svg>
                                </div>
                                <h3>Account Information</h3>
                                <p>Let's start with your personal details</p>
                            </div>

                            <div className="form-fields">
                                <div className="input-group">
                                    <label>Full Name <span className="required">*</span></label>
                                    <div className="input-wrapper">
                                        <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                        <input
                                            type="text"
                                            value={ownerName}
                                            onChange={(e) => setOwnerName(e.target.value)}
                                            disabled={loading}
                                            required
                                            placeholder="Enter your full name"
                                            className={ownerName ? 'has-value' : ''}
                                        />
                                    </div>
                                </div>

                                <div className="input-group">
                                    <label>Email Address <span className="required">*</span></label>
                                    <div className="input-wrapper">
                                        <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                            <polyline points="22,6 12,13 2,6"></polyline>
                                        </svg>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            disabled={loading}
                                            required
                                            placeholder="Enter your email address"
                                            autoComplete="email"
                                            className={email ? 'has-value' : ''}
                                        />
                                    </div>
                                </div>

                                <div className="input-row">
                                    <div className="input-group">
                                        <label>Password <span className="required">*</span></label>
                                        <div className="input-wrapper">
                                            <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                            </svg>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                disabled={loading}
                                                required
                                                placeholder="Create password"
                                                autoComplete="new-password"
                                                className={password ? 'has-value' : ''}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowPassword(!showPassword)}
                                                disabled={loading}
                                            >
                                                {showPassword ? (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                                    </svg>
                                                ) : (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                        <circle cx="12" cy="12" r="3"></circle>
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                        <small className="input-hint">Minimum 6 characters</small>
                                    </div>

                                    <div className="input-group">
                                        <label>Confirm Password <span className="required">*</span></label>
                                        <div className="input-wrapper">
                                            <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                            </svg>
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                disabled={loading}
                                                required
                                                placeholder="Confirm password"
                                                autoComplete="new-password"
                                                className={confirmPassword ? 'has-value' : ''}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                disabled={loading}
                                            >
                                                {showConfirmPassword ? (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                                    </svg>
                                                ) : (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                        <circle cx="12" cy="12" r="3"></circle>
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="input-group">
                                    <label>Phone Number <span className="optional">(Optional)</span></label>
                                    <div className="input-wrapper">
                                        <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                        </svg>
                                        <input
                                            type="tel"
                                            value={phoneNumber}
                                            onChange={(e) => setPhoneNumber(e.target.value)}
                                            disabled={loading}
                                            placeholder="Enter your phone number"
                                            className={phoneNumber ? 'has-value' : ''}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Store Information */}
                    {step === 2 && (
                        <div className={`form-step-container ${slideDirection}`}>
                            <div className="form-step-header">
                                <div className="step-icon store-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                        <polyline points="9,22 9,12 15,12 15,22"></polyline>
                                    </svg>
                                </div>
                                <h3>Store Information</h3>
                                <p>Tell us about your store</p>
                            </div>

                            <div className="form-fields">
                                <div className="input-group">
                                    <label>Store Name <span className="required">*</span></label>
                                    <div className="input-wrapper">
                                        <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                            <polyline points="9,22 9,12 15,12 15,22"></polyline>
                                        </svg>
                                        <input
                                            type="text"
                                            value={storeName}
                                            onChange={(e) => setStoreName(e.target.value)}
                                            disabled={loading}
                                            required
                                            placeholder="Enter your store name"
                                            className={storeName ? 'has-value' : ''}
                                        />
                                    </div>
                                </div>

                                <div className="input-group">
                                    <label>Store Description <span className="optional">(Optional)</span></label>
                                    <div className="input-wrapper textarea-wrapper">
                                        <svg className="input-icon textarea-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="17" y1="10" x2="3" y2="10"></line>
                                            <line x1="21" y1="6" x2="3" y2="6"></line>
                                            <line x1="21" y1="14" x2="3" y2="14"></line>
                                            <line x1="17" y1="18" x2="3" y2="18"></line>
                                        </svg>
                                        <textarea
                                            value={storeDescription}
                                            onChange={(e) => setStoreDescription(e.target.value)}
                                            disabled={loading}
                                            rows="3"
                                            placeholder="Brief description of your store and what you sell..."
                                            className={storeDescription ? 'has-value' : ''}
                                        />
                                    </div>
                                </div>

                                <div className="input-group">
                                    <label>Store Address <span className="optional">(Optional)</span></label>
                                    <div className="input-wrapper textarea-wrapper">
                                        <svg className="input-icon textarea-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                            <circle cx="12" cy="10" r="3"></circle>
                                        </svg>
                                        <textarea
                                            value={storeAddress}
                                            onChange={(e) => setStoreAddress(e.target.value)}
                                            disabled={loading}
                                            rows="2"
                                            placeholder="Enter your store address"
                                            className={storeAddress ? 'has-value' : ''}
                                        />
                                    </div>
                                </div>


                            </div>
                        </div>
                    )}

                    {/* Navigation buttons */}
                    <div className="form-navigation">
                        {step > 1 && (
                            <button
                                type="button"
                                onClick={handlePrevious}
                                disabled={loading}
                                className="nav-button secondary"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="19" y1="12" x2="5" y2="12"></line>
                                    <polyline points="12,19 5,12 12,5"></polyline>
                                </svg>
                                Previous
                            </button>
                        )}

                        {step < 2 ? (
                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={loading}
                                className="nav-button primary"
                            >
                                Continue
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                    <polyline points="12,5 19,12 12,19"></polyline>
                                </svg>
                            </button>
                        ) : (
                            <button
                                type="submit"
                                disabled={loading}
                                className={`nav-button primary ${loading ? 'loading' : ''}`}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner"></span>
                                        Creating Your Store...
                                    </>
                                ) : (
                                    <>
                                        Create My Store
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                            <polyline points="22,4 12,14.01 9,11.01"></polyline>
                                        </svg>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </form>

                <div className="register-footer">
                    <p>Already have an account? <a href="/admin/login">Sign In</a></p>
                </div>
            </div>

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="success-modal-overlay">
                    <div className="success-modal">
                        <div className="success-animation">
                            <div className="success-checkmark">
                                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path className="checkmark-circle" d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                    <polyline className="checkmark-check" points="22,4 12,14.01 9,11.01"></polyline>
                                </svg>
                            </div>
                        </div>
                        <h2>🎉 Congratulations!</h2>
                        <p className="success-title">Your store has been created successfully!</p>
                        <div className="success-details">
                            <div className="success-store-info">
                                <div className="success-avatar">
                                    {storeName ? storeName.charAt(0).toUpperCase() : 'S'}
                                </div>
                                <div>
                                    <strong>{storeName}</strong>
                                    <span>{email}</span>
                                </div>
                            </div>
                        </div>
                        <p className="success-message">{successMessage}</p>
                        <div className="success-actions">
                            <button
                                className="success-button primary"
                                onClick={handleGoToLogin}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                                    <polyline points="10,17 15,12 10,7"></polyline>
                                    <line x1="15" y1="12" x2="3" y2="12"></line>
                                </svg>
                                Go to Login
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminRegisterPage;